import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { DatePickerShortcut } from '@va/types/time';
import { TooltipWrapper } from '@va/ui/tooltips';
import { getDateRange, getTimezone } from '@va/util/helpers';
import { useWindowDimensions } from '@va/util/hooks';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useDatePickerContext } from '../context';

export const Shortcut = () => {
  const translate = useTranslate();
  const { isTablet } = useWindowDimensions();
  const {
    shortcuts,
    tooltipContent,
    trackedDataStartDate,
    applyShortcut,
    shouldDisablePastDates,
    minDate,
    state,
    value,
  } = useDatePickerContext();

  const timezone = useMemo(() => (value ? getTimezone(value?.[0]) : null), [value]);

  const setToDate = useCallback(
    (item: DatePickerShortcut) => {
      const { start, end } = getDateRange(item.key, item.diff, trackedDataStartDate, timezone);
      applyShortcut(start, end, item.key);
    },
    [applyShortcut, timezone, trackedDataStartDate],
  );

  const isShortcutDisabled = useCallback(
    (item: DatePickerShortcut) => {
      const { start } = getDateRange(item.key, item.diff, trackedDataStartDate, timezone);
      return minDate && start.valueOf() < dayjs(minDate).startOf('day').valueOf();
    },
    [minDate, trackedDataStartDate, timezone],
  );

  const isShortcutSelected = useCallback(
    (item: DatePickerShortcut) => {
      const { start, end } = getDateRange(item.key, item.diff, trackedDataStartDate, timezone);
      const [started, ended] = state.pendingValue;

      const finalValue = started && ended && started.valueOf() === start.valueOf() && ended.valueOf() === end.valueOf();
      return finalValue;
    },
    [state.pendingValue, trackedDataStartDate, timezone],
  );

  const onShortcutClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>, item: DatePickerShortcut) => {
      e.preventDefault();
      setToDate(item);
    },
    [setToDate],
  );

  return (
    <div className='btn-class pb-2 shortcut-wrapper relative w-full border-r border-r-black/10 lg:mr-1 lg:mb-0 lg:mx-0'>
      <ol className='shortcut grid grid-cols-2 gap-1 lg:flex lg:flex-col w-full pr-0 mt-1.5 lg:mb-0'>
        {shortcuts.map((item, i) => {
          const disabled = shouldDisablePastDates ? isShortcutDisabled(item) : false;
          return (
            <TooltipWrapper
              arrow
              interactive
              placement={isTablet ? 'auto' : 'left'}
              disabled={!disabled}
              content={!!tooltipContent && tooltipContent}
              key={i}
            >
              <li data-testid={TEST_IDS.helpers.createListItemId(item.key)} key={i} className={'relative mt-0.25'}>
                <button
                  className={classNames(
                    'text-sm lg:text-xs px-2 py-2 shortcut-lineheight whitespace-nowrap font-semibold rounded-sm transition-colors btn-class text-start text-wrap w-full',
                    {
                      'cursor-pointer text-primary hover:bg-white-snow focus-visible:bg-primary-100 focus-visible:text-primary dark:hover:bg-primary-700 dark:hover:text-primary dark:text-primary dark:focus-visible:bg-primary-700 dark:focus-visible:text-primary focus-visible:bg-primary-50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-hidden ':
                        !disabled,
                    },
                    { 'hover:transparent text-gray-disabled cursor-not-allowed': disabled },
                    {
                      'shortcut-class ring-3 ring-primary outline-hidden': isShortcutSelected(item),
                    },
                  )}
                  onClick={(e) => !disabled && onShortcutClick(e, item)}
                >
                  {translate(`${item.transKey}`, { interval: item.diff })}
                  {item.isDefault && <span className='ml-1'>({translate('datePicker.default')})</span>}
                </button>
              </li>
            </TooltipWrapper>
          );
        })}
      </ol>
    </div>
  );
};
